<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card active-quote-compare">
        <h5>Dashboard Settings</h5>
        <h6>Active quotes: compare with yesterday</h6>
        <p class="aqc-intro">
          Each night at 23:30 the CRM keeps a copy of the active quotes. The All Active Quotes tiles can then show
          how today compares with yesterday, with the same filters.
        </p>

        <small class="p-invalid" v-if="loadError">{{ loadError }}</small>

        <div class="p-field">
          <label for="aqcFrom">Active-quote comparison shown from</label>
          <div class="p-d-flex p-ai-center">
            <Calendar id="aqcFrom" v-model="date" dateFormat="dd/mm/yy" :showIcon="true" :showButtonBar="true"
                      :disabled="loading || !!loadError" class="p-mr-2" />
          </div>
          <small class="hint">Leave blank to keep the comparison hidden. Before this date the tiles show no comparison.</small>
          <small class="aqc-status">{{ statusLine }}</small>
        </div>

        <div class="p-field">
          <label for="aqcRetention">Keep snapshots for (days)</label>
          <div class="p-d-flex p-ai-center">
            <InputNumber id="aqcRetention" v-model="retentionDays" :min="minRetentionDays" :useGrouping="false"
                         :disabled="loading || !!loadError" class="p-mr-2" />
          </div>
          <small class="hint">Older snapshots are removed each night. At least {{ minRetentionDays }} days.</small>
        </div>

        <Button v-if="!loadError" data-test="save-active-quote-compare" label="Save" icon="pi pi-check"
                :disabled="loading || saving || !dirty" @click="save" />

        <div class="aqc-days p-mt-4" v-if="!loadError && !loading">
          <div>Snapshots held: {{ daysHeld }} {{ daysHeld === 1 ? 'day' : 'days' }}</div>
          <ul v-if="latestDays.length">
            <li v-for="d in latestDays" :key="d.date">{{ display(d.date) }}: {{ d.rows }} active quotes</li>
          </ul>
          <Button v-if="isAdmin" data-test="take-snapshot" class="p-button-outlined p-mt-2"
                  label="Take today's snapshot now" icon="pi pi-camera" :disabled="taking" @click="takeNow" />
        </div>

        <!-- Widget Builder left the side menu (Mohammed 04/10/2026); Admin opens it from here. The
             /widgetBuilder route keeps its own Dashboard-ComposeWidgets check. -->
        <div v-if="canBuildWidgets" class="aqc-widgets p-mt-5">
          <h6>Dashboard widgets</h6>
          <p class="aqc-intro">Build a new widget for the dashboards, or change one already built.</p>
          <Button id="openWidgetBuilder" data-test="open-widget-builder" label="Build or edit widgets"
                  icon="pi pi-sliders-h" class="p-button-outlined" @click="openWidgetBuilder" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSettingsService from '@/service/AppSettingsService'
import { toIsoDate, fromIsoDate, displayDate } from '@/mbp/followupSetting/bankHolidays/bankHolidayDates'

function messageOf(err, fallback) {
  var data = err && err.response && err.response.data
  if (data && typeof data.error === 'string' && data.error) return data.error
  return fallback
}

export default {
  name: 'ActiveQuoteCompareSettings',
  data: () => ({
    appSettingsService: new AppSettingsService(),
    date: null,
    retentionDays: 400,
    minRetentionDays: 32,
    saved: { compareFrom: null, retentionDays: 400 },
    shownNow: false,
    daysHeld: 0,
    latestDays: [],
    loading: true,
    saving: false,
    taking: false,
    loadError: null
  }),
  computed: {
    dirty() {
      return toIsoDate(this.date) !== this.saved.compareFrom || Number(this.retentionDays) !== this.saved.retentionDays
    },
    isAdmin() {
      const profile = this.$store && this.$store.getters['auth/getMyProfile']
      return !!(profile && profile.role && profile.role.showAdmin)
    },
    // Admin only, and only with the permission the widget builder's route checks.
    canBuildWidgets() {
      const g = (this.$store && this.$store.getters) || {}
      const profile = g['auth/getMyProfile']
      const isAdminRole = !!(profile && profile.role && profile.role.roleName === 'Admin')
      const held = (g['auth/getUserPermissions'] || [])
        .find(a => a && a.permission && a.permission.permissionCode === 'Dashboard-ComposeWidgets')
      return isAdminRole && !!held && held.permissionLevel !== 0
    },
    statusLine() {
      if (!this.saved.compareFrom) return 'Off: the tiles show no comparison.'
      if (this.shownNow) return 'Showing on the dashboard since ' + displayDate(this.saved.compareFrom) + '.'
      return 'Will show from ' + displayDate(this.saved.compareFrom) + '.'
    }
  },
  created() {
    this.load()
  },
  methods: {
    openWidgetBuilder() {
      this.$router.push({ name: 'widgetBuilder' }).catch(() => {})
    },
    display(iso) {
      return displayDate(iso)
    },
    apply(data) {
      const d = data || {}
      this.saved = {
        compareFrom: d.compareFrom || null,
        retentionDays: Number(d.retentionDays) || 400
      }
      this.date = fromIsoDate(this.saved.compareFrom)
      this.retentionDays = this.saved.retentionDays
      if (d.minRetentionDays) this.minRetentionDays = Number(d.minRetentionDays)
      this.shownNow = !!d.shownNow
      this.daysHeld = Number(d.daysHeld) || 0
      this.latestDays = Array.isArray(d.latestDays) ? d.latestDays : []
    },
    load() {
      this.loading = true
      return this.appSettingsService.getActiveQuoteCompare()
        .then(data => {
          this.apply(data)
          this.loadError = null
        })
        .catch(err => {
          this.loadError = messageOf(err, 'The dashboard settings could not be loaded.')
        })
        .finally(() => { this.loading = false })
    },
    save() {
      this.saving = true
      return this.appSettingsService.saveActiveQuoteCompare({
        compareFrom: toIsoDate(this.date),
        retentionDays: Number(this.retentionDays)
      })
        .then(data => {
          this.apply(data)
          this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'The dashboard settings were saved.', life: 5000 })
        })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not saved', detail: messageOf(err, 'The dashboard settings could not be saved.'), life: 5000 })
        })
        .finally(() => { this.saving = false })
    },
    takeNow() {
      // D42: :disabled only applies after the re-render; a second click in the same tick got through
      if (this.taking) return Promise.resolve()
      this.taking = true
      return this.appSettingsService.takeActiveQuoteSnapshot()
        .then(run => {
          this.$toast.add({
            severity: 'success',
            summary: 'Snapshot taken',
            detail: displayDate(run && run.date) + ': ' + ((run && run.rows) || 0) + ' active quotes kept.',
            life: 5000
          })
          return this.load()
        })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not taken', detail: messageOf(err, 'The snapshot could not be taken.'), life: 5000 })
        })
        .finally(() => { this.taking = false })
    }
  }
}
</script>

<style>
.active-quote-compare .hint,
.active-quote-compare .aqc-status { display: block; color: #6c757d; margin-top: 0.25rem; }
.active-quote-compare .aqc-intro { max-width: 46rem; }
.active-quote-compare small.p-invalid { display: block; color: #d32f2f; }
.active-quote-compare .aqc-days ul { margin: 0.25rem 0 0; padding-left: 1.25rem; }
</style>
